/**
 * Groups API - Update Group
 *
 * PATCH /v1/groups/:groupId
 */

import { z } from 'zod'
import type { GroupDesignTheme } from './common'
import { AppVisibilitySchema } from '../spaces/common'
import type { AppVisibility } from '../spaces/common'

// ============================================================================
// PATCH /v1/groups/:groupId
// ============================================================================

/**
 * The community (custom UserGroup) write body the unified space-detail dialog
 * sends to `PUT /v1/groups/:id`. A curated subset of `UpdateGroupRequest` — only
 * the fields the shared-core tabs edit. `bannerBackgroundColor` is non-nullable
 * (the endpoint accepts `z.string().optional()`; a clear is the empty string).
 * Lives in shared-types because API wire types are shared (web rule).
 */
export interface CommunityWriteBody {
  name?: string
  displayName?: string
  description?: string
  designTheme?: GroupDesignTheme | null
  bannerBackgroundColor?: string
  bannerLogoUrl?: string | null
  bannerImageUrl?: string | null
  avatarImageUrl?: string | null
  chatEnabled?: boolean
  memberPostingEnabled?: boolean
  commentsEnabled?: boolean
  reactionsEnabled?: boolean
  sharingEnabled?: boolean
  mediaLibraryEnabled?: boolean
  memberListEnabled?: boolean
  showInApp?: boolean
  betaTestersOnly?: boolean
  appVisibility?: AppVisibility
}

export const UpdateGroupRequestSchema = z.object({
  name: z.string().optional(),
  description: z.string().optional(),
  displayName: z.string().optional(),
  groupType: z.string().optional(),
  audience: z.string().optional(),
  bannerBackgroundColor: z.string().optional(),
  bannerLogoUrl: z.string().nullable().optional(),
  bannerImageUrl: z.string().nullable().optional(),
  avatarImageUrl: z.string().nullable().optional(),
  designTheme: z.record(z.string(), z.any()).nullable().optional(),
  sourceSyncEnabled: z.boolean().optional(),
  memberListEnabled: z.boolean().optional(),
  memberPostingEnabled: z.boolean().optional(),
  commentsEnabled: z.boolean().optional(),
  reactionsEnabled: z.boolean().optional(),
  sharingEnabled: z.boolean().optional(),
  chatEnabled: z.boolean().optional(),
  mediaLibraryEnabled: z.boolean().optional(),
  showInApp: z.boolean().optional(),
  betaTestersOnly: z.boolean().optional(),
  // 3-state visibility override. Takes precedence over showInApp when both are
  // present; the backend derives app_visibility from showInApp when absent.
  appVisibility: AppVisibilitySchema.optional(),
  contactEnabled: z.boolean().optional(),
  // Announcement broadcast mode. Additive/optional. Toggling ON for an already
  // populated channel is deferred (Stream channel type is immutable in place);
  // the backend update use-case (U5) gates which transitions it honors.
  announcementMode: z.boolean().optional()
})

export type UpdateGroupRequest = z.infer<typeof UpdateGroupRequestSchema>

export const UpdateGroupResponseSchema = z.object({
  message: z.string(),
  data: z.object({
    publicId: z.string(),
    name: z.string(),
    createdAt: z.string(),
    updatedAt: z.string(),
    description: z.string().optional(),
    displayName: z.string().optional(),
    groupType: z.string(),
    audience: z.string().optional(),
    bannerBackgroundColor: z.string().optional(),
    bannerLogoUrl: z.string().nullable().optional(),
    bannerImageUrl: z.string().nullable().optional(),
    avatarImageUrl: z.string().nullable().optional(),
    designTheme: z.record(z.string(), z.any()).optional(),
    memberListEnabled: z.boolean(),
    memberPostingEnabled: z.boolean(),
    // Member media contribute toggle (plan 2026-07-22-002 U5) — echoed on the
    // response. `.optional()` for deploy skew (see sections/update.ts).
    memberMediaEnabled: z.boolean().optional(),
    commentsEnabled: z.boolean(),
    reactionsEnabled: z.boolean(),
    sharingEnabled: z.boolean(),
    chatEnabled: z.boolean(),
    mediaLibraryEnabled: z.boolean(),
    showInApp: z.boolean(),
    contactEnabled: z.boolean(),
    // Required (not optional): the backend update response always serializes
    // `announcementMode` as a boolean — align the published contract with the
    // server so consumers branch on a guaranteed boolean (review finding #14).
    announcementMode: z.boolean()
  })
})

export type UpdateGroupResponse = z.infer<typeof UpdateGroupResponseSchema>

// ============================================================================
// DELETE /v1/groups/:groupId
// ============================================================================

export const DeleteGroupResponseSchema = z.object({
  message: z.string(),
  data: z.object({
    groupId: z.string(),
    status: z.literal('DELETED'),
    deletedAt: z.string()
  })
})

export type DeleteGroupResponse = z.infer<typeof DeleteGroupResponseSchema>
